import type { ptBR } from './pt-BR';

export const en: typeof ptBR = {
  appName: 'GBFR Save Analytics',
  tagline: 'data analytics & correction',
  open: 'Open save',
  compareWith: 'Compare with…',
  download: 'Download corrected save',
  downloading: 'Recalculating hash…',
  undoAll: 'Undo all',
  language: 'Language',
  fileInfo: '{name} · {size} MB · {units} units',

  navAnalysis: 'Analysis',
  navData: 'Data',
  dashboard: 'Dashboard',
  characters: 'Characters',
  sigils: 'Sigils',
  materials: 'Materials',
  weapons: 'Weapons',
  otherItems: 'Other items',
  explore: 'Explore all',
  ids: 'ID table',
  compare: 'Compare',
  changes: 'Changes',

  welcomeTitle: 'Read your save before you touch it.',
  welcomeBody: 'Open Granblue Fantasy: Relink’s SaveData1.dat to see characters, sigils, materials and weapons, compare two saves and, if you want, correct values. The file is read only in this browser: nothing is uploaded anywhere.',
  dropHere: 'Drop SaveData1.dat here',
  orChoose: 'or choose the file',
  whereIsSave: 'The save lives in the game’s SaveGames folder, next to the SaveData1_BackUp.dat and SaveData1_BackUp2.dat backups.',
  step1: 'Open the save and check the integrity seal.',
  step2: 'Read the dashboard; compare with a backup to see what changed.',
  step3: 'Edit carefully, download the corrected save and replace it with the game closed.',
  readError: 'Could not read {name}: it does not look like a Relink save.',
  configFile: 'This is a settings file, not the progress save. Use “Explore all” to view and edit its fields.',

  responsibilityTitle: 'Before you start',
  responsibilityBody: 'This is a data analytics and correction tool. Any change you make to your files is entirely your own responsibility. Always back up the original save before replacing it.',
  responsibilityPoints1: 'The save is processed only in your browser; nothing is uploaded.',
  responsibilityPoints2: 'Many fields have uncertain meaning; each one shows its confidence.',
  responsibilityPoints3: 'Fan project, not affiliated with Cygames.',
  responsibilityAccept: 'I understand and accept responsibility',
  footerNotice: 'Data analytics and correction tool. You are entirely responsible for any change to your files. Not affiliated with Cygames.',
  downloadNotice: 'By downloading you take responsibility for the changes. Close the game and keep the original.',
  namesCredit: 'Names: Nenkai/relink-modding',
  namesOffline: 'Name list unavailable; showing IDs.',
  namesLoading: 'Loading names…',
  source: 'Source code',
  madeBy: 'Made by',
  music: 'Theme music',
  musicTurnOn: 'Turn theme music on',
  musicTurnOff: 'Turn theme music off',
  sfx: 'Sound effects',
  sfxTurnOn: 'Turn sound effects on',
  sfxTurnOff: 'Turn sound effects off',

  inParty: 'In current party',
  sigilsWorn: 'Equipped sigils',
  noWeapons: 'No weapons for this character in the save.',
  noSigils: 'No sigils equipped.',
  sheetNote: 'Values as read from the save. To change them, use the edit table.',
  editCharacter: 'Edit character',
  prevChar: 'Previous character',
  nextChar: 'Next character',
  close: 'Close',

  sealOk: 'Hash verified',
  sealBad: 'Hash invalid',
  sealFixed: 'Hash recalculated',
  sealNone: 'No hash',
  sealOkBody: 'XXHash64 integrity (#{index}) matches the file.',
  sealBadBody: 'Hash #{index} does not match; the game would report the save as corrupted. It will be fixed on download.',
  sealFixedBody: 'Hash #{index} recalculated for your changes.',
  sealNoneBody: 'This file has no integrity hash.',
  sealPending: 'Hash pending',
  stamp_ok: 'valid', stamp_bad: 'fail', stamp_fixed: 'new', stamp_pending: 'pending', stamp_none: 'no hash',
  sealPendingBody: 'There are changes; the hash will be recalculated on download.',
  confidence: 'Confidence',
  confirmed: 'confirmed',
  likely: 'likely',
  possible: 'possible',
  unknown: 'unknown',

  rupies: 'Rupies',
  maxedChars: 'Level 100',
  unknownChar: 'Unknown character',
  maxShort: 'MAX',
  noOwner: 'No identified owner',
  howItWorks: 'How it works',
  analysis: 'Save analysis',
  charEditTitle: 'Edit characters',
  forGame: 'for Granblue Fantasy: Relink',
  artCredit: 'Artwork and logo © Cygames, used in a fan project.',
  sigilCount: 'Sigils',
  materialKinds: 'Materials',
  weaponCount: 'Weapons',
  sinceCompare: '{delta} since {name}',
  playerName: 'Protagonist',
  profileName: 'Profile name',
  party: 'Current party',
  lastSaved: 'Saved at',

  charProgress: 'Character progress',
  charProgressNote: 'Level of each recruited character; gold = max level.',
  level: 'Level',
  exp: 'EXP',
  hp: 'HP',
  atk: 'Attack',
  msp: 'MSP',
  progress: 'Progress',
  character: 'Character',
  charEditNote: 'When changing level, adjust EXP too (level 100 = 8,400,000), or the game may recalculate. HP, Attack and MSP are guesses.',

  sigilLevels: 'Sigils by level',
  topSigils: 'Most numerous sigils',
  sigilsByChar: 'Sigils equipped per character',
  sigil: 'Sigil',
  wornBy: 'Worn by',
  serial: 'Serial',
  count: 'Count',
  duplicates: 'duplicates',

  topMaterials: 'Most abundant materials',
  scarceMaterials: 'Nearly depleted materials',
  material: 'Material',
  quantity: 'Quantity',

  weaponsByChar: 'Weapons per character',
  weapon: 'Weapon',
  owner: 'Owner',
  uncap: 'Uncap',
  awakening: 'Awakening',
  wrightstone: 'Wrightstone',
  appearance: 'Appearance',

  evolution: 'Evolution between saves',
  evolutionEmpty: 'Open a second save with “Compare with…” (e.g. SaveData1_BackUp2.dat) to see what you gained, spent and levelled.',
  rupiesDelta: 'Rupies',
  materialDeltas: 'Materials gained and spent',
  levelUps: 'Levels gained',
  sigilsGained: 'More sigils',
  weaponsGained: 'More weapons',
  gained: 'gained',
  spent: 'spent',
  sideA: 'Side A · {name}',
  sideB: 'Side B · {name}',

  wrightstones: 'Wrightstones',
  curios: 'Curios',
  list1901: 'List 1901 (uncertain)',

  filter: 'Filter by name or ID…',
  filterValue: 'Value, name or description…',
  shown: '{shown} of {total}',
  tooMany: '{total} units; showing {shown}. Narrow the filter.',
  allTypes: 'all types',
  allSections: 'all sections',
  onlyNonZero: 'non-zero only',
  section: 'section',
  type: 'type',
  value: 'value',
  description: 'description',
  edit: 'edit',
  showing: 'Showing {n} of {total} values.',
  empty: '(empty)',
  none: '—',
  viewTable: 'View as table',

  idsIntro: 'Each id is a kind of data; the unit separates entries (an item, a character…). Your notes are stored in this browser.',
  group: 'group',
  note: 'note',
  notePlaceholder: 'your note…',
  onlyDescribed: 'only with description or note',
  exportNotes: 'Export notes',
  importNotes: 'Import notes',
  importError: 'Could not import: {msg}',
  view: 'view',
  example: 'example',
  noDescription: 'no description',

  compareIntro: 'Pick another save with “Compare with…”. Tip: copy the save, do a single thing in game, save and compare to discover an ID.',
  differences: 'Differences: {a} → {b} ({n})',
  refersTo: 'refers to',
  change: 'change',
  compareFirst: 'Open the main save first.',

  changesTitle: 'Pending changes ({n})',
  noChanges: 'No changes yet.',
  field: 'field',
  undo: 'undo',
  changesHelp: 'To apply: click “Download corrected save”, close the game and replace {name} in the saves folder, keeping the original. The downloaded file has the same size; only the edited bytes and the integrity hash change.',

  errNan: 'Not a number.',
  errInt: 'Must be a whole number.',
  errRange: 'Out of range {lo}..{hi}.',
  unsavedWarning: 'There are changes not downloaded yet.',

  groupplayer: 'Player', groupprogress: 'Progress', groupintegrity: 'Integrity', groupworld: 'World', groupdates: 'Dates',
  grouponline: 'Online', groupparty: 'Party', groupcharacters: 'Characters', groupmaterials: 'Materials', groupitems: 'Items',
  groupwrightstones: 'Wrightstones', groupsigils: 'Sigils', groupweapons: 'Weapons', groupcollection: 'Collection', grouploadscreen: 'Load screen',
};
